import React, { useEffect, useState } from 'react';
import { inferSessionContext, type SessionPlanningInference } from '@interactive-room';
import type { SessionConfig } from '../../types/sessionArchetypes';

const DEBOUNCE_MS = 500;

interface SessionContextInferencePanelProps {
  foco: string;
  evidencia: string;
  title: string;
  sessionConfig: SessionConfig;
  subjectTag: string;
  enabled?: boolean;
}

export function SessionContextInferencePanel({
  foco,
  evidencia,
  title,
  sessionConfig,
  subjectTag,
  enabled = true,
}: SessionContextInferencePanelProps) {
  const [inference, setInference] = useState<SessionPlanningInference | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!enabled || String(subjectTag || '').toLowerCase() !== 'matematica') {
      setInference(null);
      setLoading(false);
      return undefined;
    }

    const combined = [foco, evidencia, title].join(' ').trim();
    if (combined.length < 8) {
      setInference(null);
      setLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setLoading(true);

    const timer = window.setTimeout(async () => {
      try {
        const result = await inferSessionContext({
          sessionObjective: foco,
          sessionSummary: evidencia,
          sessionTitle: title,
          sessionConfig,
          subjectTag,
          signal: controller.signal,
        });
        if (!controller.signal.aborted) {
          setInference(result);
          setLoading(false);
        }
      } catch {
        if (!controller.signal.aborted) {
          setInference(null);
          setLoading(false);
        }
      }
    }, DEBOUNCE_MS);

    return () => {
      controller.abort();
      window.clearTimeout(timer);
      setLoading(false);
    };
  }, [enabled, foco, evidencia, title, sessionConfig, subjectTag]);

  if (!enabled || String(subjectTag || '').toLowerCase() !== 'matematica') {
    return null;
  }

  const combined = [foco, evidencia, title].join(' ').trim();
  if (combined.length < 8) {
    return (
      <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/80 px-3 py-2.5 text-xs text-gray-500">
        Escribe el foco pedagógico para ver qué contexto detectará la guía de medición.
      </div>
    );
  }

  if (loading && !inference) {
    return (
      <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 px-3 py-2.5 text-xs text-emerald-800">
        Analizando tu plan…
      </div>
    );
  }

  if (!inference?.relevant) {
    return (
      <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-xs text-gray-600">
        No detectamos medición de longitud en este foco. El panel se activa para guías OA 20 /
        conversión de unidades.
      </div>
    );
  }

  const measuresText =
    inference.measureDisplay.length > 0
      ? inference.measureDisplay.join(', ')
      : 'Sin medidas numéricas aún — se usará el escenario del entorno';

  return (
    <aside
      className="space-y-2 rounded-lg border border-emerald-200 bg-emerald-50/60 px-3 py-3 text-xs text-emerald-950"
      aria-live="polite"
    >
      <p className="font-medium text-emerald-900">Contexto para la guía (vista previa)</p>
      <ul className="space-y-1.5 leading-relaxed">
        <li>
          <span aria-hidden>✅ </span>
          <strong>Contexto detectado:</strong> {inference.entorno}
        </li>
        <li>
          <span aria-hidden>📏 </span>
          <strong>Medidas halladas:</strong> {measuresText}
        </li>
        <li>
          <span aria-hidden>💡 </span>
          <strong>Acción:</strong> {inference.actionMessage}
        </li>
      </ul>
      <p className="text-[10px] text-emerald-800/80">
        Hito: {inference.milestoneLabel} · Fuente: {inference.sourceLabel}
        {loading ? ' · actualizando…' : ''}
      </p>
    </aside>
  );
}
